// src/components/roles/RolesPage/RolesPage.tsx
import React from 'react';
import { useSelector } from 'react-redux';
import {
  selectCurrentUser,
  selectHasRole,
  getAuthRoleName,
} from '../../../redux/slices/auth.slice';
import { Box, Alert, CircularProgress } from '@mui/material';
import {
  Add as AddIcon,
  CloudDownload as DownloadIcon,
  DeleteOutline as DeleteIcon,
} from '@mui/icons-material';
import {
  ListPageScaffold,
  ListPageMain,
  ListPageToolbar,
  ListPageFiltersSection,
} from '../../common/list-page';
import RoleFilters from '../RoleFilters';
import RolesTable from '../RolesTable';
import CommonPagination from '../../common/Pagination';
import { RoleWizard } from '../RoleWizard';
import EmptyStateMessage from '../../common/EmptyStateMessage';
import RoleDetailModal from '../RoleDetailModal';
import RoleDeleteModal from '../RoleDeleteModal';
import RoleBlockModal from '../RoleBlockModal';
import { useRolesPage } from './RolesPage.hooks';
import { useSettingsRolesPermissions } from '../../../hooks/useSettingsRolesPermissions';
import {
  PrimaryButton,
  SecondaryButton,
  DangerButton,
  StyledSnackbar,
  StyledAlert,
} from './RolesPage.styles';

const RolesPage: React.FC = () => {
  // Role management permissions - centralized in a single hook
  const rolesPerms = useSettingsRolesPermissions();

  // Determinar si el usuario es Super Admin
  const currentUser = useSelector(selectCurrentUser);
  const hasSuperAdminRole = useSelector(selectHasRole('Super Admin'));
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const isSuperAdmin =
    hasSuperAdminRole ||
    currentUser?.roles?.some((role) => getAuthRoleName(role) === 'Super Admin') ||
    false;

  const {
    // Filter states
    filters,
    setFilters,
    appliedFilters,
    showMoreFilters,
    setShowMoreFilters,

    // Pagination states
    pagination,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setPagination,

    // Drawer states
    drawerOpen,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setDrawerOpen,
    selectedRole,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setSelectedRole,

    // Modal states
    detailModalOpen,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setDetailModalOpen,
    detailRole,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setDetailRole,
    deleteModalOpen,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setDeleteModalOpen,
    rolesToDelete,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setRolesToDelete,
    blockModalOpen,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setBlockModalOpen,
    roleToBlock,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setRoleToBlock,

    // Role data
    roles,
    selectedRoles,
    setSelectedRoles,
    loading,
    error,

    // Notification state
    notification,

    // Handlers
    handleSearch,
    handleReset,
    handleClearAppliedField,
    handleFilterChange,
    handlePaginationChange,
    handleItemsPerPageChange,
    handleAddRole,
    handleEditRole,
    handleViewRole,
    handleDeleteRoles,
    handleDeleteSingleRole,
    handleConfirmDelete,
    handleCloseDeleteModal,
    handleBlockRole,
    handleBlockRoleDirect,
    handleUnblockRole,
    handleConfirmBlock,
    handleCloseBlockModal,
    handleCloseDrawer,
    handleSaveRole,
    handleCloseDetailModal,
    handleCloseNotification,
    handleExportRoles,
    handleCopyRole,
  } = useRolesPage();

  // Toggle more filters
  const toggleMoreFilters = () => {
    setShowMoreFilters(!showMoreFilters);
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    handlePaginationChange({ currentPage: page });
  };

  // Handle delete selected
  const handleDeleteSelected = () => {
    handleDeleteRoles();
  };

  return (
    <ListPageScaffold>
      <ListPageMain sectionGap={1}>
        <ListPageToolbar>
          {rolesPerms.canAddRole && (
            <PrimaryButton
              variant="contained"
              startIcon={<AddIcon />}
              size="small"
              onClick={handleAddRole}
            >
              Añadir
            </PrimaryButton>
          )}

          <SecondaryButton
            variant="outlined"
            startIcon={<DownloadIcon />}
            size="small"
            onClick={handleExportRoles}
          >
            Exportar
          </SecondaryButton>

          {rolesPerms.canDeleteRole && (
            <DangerButton
              variant="outlined"
              startIcon={<DeleteIcon />}
              size="small"
              onClick={handleDeleteSelected}
              disabled={selectedRoles.length === 0 || loading}
            >
              Eliminar
            </DangerButton>
          )}
        </ListPageToolbar>

        <ListPageFiltersSection>
          <RoleFilters
            filters={filters}
            setFilters={setFilters}
            appliedFilters={appliedFilters}
            onClearAppliedField={handleClearAppliedField}
            showMoreFilters={showMoreFilters}
            onSearch={handleSearch}
            onReset={handleReset}
            onToggleMoreFilters={toggleMoreFilters}
            onFilterChange={handleFilterChange}
          />
        </ListPageFiltersSection>

        {/* Error message */}
        {error && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              p: 4,
              minHeight: '200px',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <Alert severity="error">{error}</Alert>
          </Box>
        )}

        {/* Loading state */}
        {loading && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              p: 4,
              minHeight: '200px',
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {/* Empty state */}
        {!loading && roles.length === 0 && !error && (
          <EmptyStateMessage message="No se encontraron roles" minHeight={200} />
        )}

        {/* Roles table */}
        {!loading && roles.length > 0 && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 0,
              border: 'none',
              bgcolor: 'transparent',
              overflow: 'visible',
              minHeight: 0,
            }}
          >
            <RolesTable
              roles={roles}
              loading={loading}
              selectedRoles={selectedRoles}
              setSelectedRoles={setSelectedRoles}
              onViewRole={handleViewRole}
              onEditRole={handleEditRole}
              onDeleteRole={handleDeleteSingleRole}
              onCopyRole={handleCopyRole}
              onBlockRole={handleBlockRole}
              onBlockRoleDirect={handleBlockRoleDirect}
              onUnblockRoleDirect={handleUnblockRole}
            />
          </Box>
        )}

        {/* Pagination */}
        {!loading && roles.length > 0 && (
          <Box>
            <CommonPagination
              currentPage={pagination.currentPage}
              itemsPerPage={pagination.itemsPerPage}
              totalItems={pagination.totalItems}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          </Box>
        )}
      </ListPageMain>

      {/* Role Wizard */}
      <RoleWizard
        isOpen={drawerOpen}
        onClose={handleCloseDrawer}
        onSave={handleSaveRole}
        editRole={selectedRole}
      />

      {/* Role Detail Modal */}
      <RoleDetailModal
        open={detailModalOpen}
        role={detailRole}
        onClose={handleCloseDetailModal}
        onEditRole={handleEditRole}
      />

      {/* Delete Confirmation Modal */}
      <RoleDeleteModal
        open={deleteModalOpen}
        roles={rolesToDelete}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
      />

      {/* Block Confirmation Modal */}
      <RoleBlockModal
        open={blockModalOpen}
        role={roleToBlock}
        onClose={handleCloseBlockModal}
        onConfirm={handleConfirmBlock}
      />

      {/* Notification Snackbar */}
      <StyledSnackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {notification.successMessage && notification.errorMessage ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              width: '100%',
            }}
          >
            <StyledAlert
              onClose={handleCloseNotification}
              severity="success"
              variant="filled"
              sx={{ width: '100%' }}
            >
              {notification.successMessage}
            </StyledAlert>
            <StyledAlert
              onClose={handleCloseNotification}
              severity="error"
              variant="filled"
              sx={{ width: '100%' }}
            >
              {notification.errorMessage}
            </StyledAlert>
          </Box>
        ) : (
          <StyledAlert
            onClose={handleCloseNotification}
            severity={notification.severity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {notification.message}
          </StyledAlert>
        )}
      </StyledSnackbar>
    </ListPageScaffold>
  );
};

export default RolesPage;
